import { Eye, EyeOff, Wallet } from 'lucide-react';
import { tr } from '../../lib/i18n.ts';
import { changePercent, formatPercent, formatTry } from '../../apps/kasa/money.ts';
import { useNetWorth } from '../../apps/kasa/queries.ts';
import { useLaunch } from '../../desktop/launch.ts';
import { useShows } from '../BarPlanContext.tsx';
import { useInfoBarPrefs, usePrefsStore } from '../prefsStore.ts';
import type { WidgetVariant } from './types.ts';

const MASK = '•••••';

/** Shown once the user has a Kasa account (or turned it on); the amount stays masked until revealed. */
export function NetWorthWidget({ variant }: { variant: WidgetVariant }) {
  const { widgets, hideNetWorth } = useInfoBarPrefs();
  const launch = useLaunch();
  const summary = useNetWorth(widgets.netWorth !== false);
  const data = summary.data;
  const shows = useShows();
  if (variant === 'bar' && !shows('netWorth')) return null;
  if (!data || (widgets.netWorth === undefined && data.accountCount === 0)) return null;

  const change = changePercent(data.netWorth.totalTryMinor, data.previousTryMinor);
  const total = data.netWorth.totalTryMinor;
  return (
    <div className="iw iw-networth" data-piece="netWorth" data-variant={variant}>
      <button type="button" className="iw-nw-open" onClick={() => launch('kasa')} aria-label={tr.infobar.netWorth.open}>
        <Wallet size={15} aria-hidden />
        <span className="iw-nw-label">{tr.infobar.widget.netWorth}</span>
        {hideNetWorth ? (
          <>
            <span className="iw-nw-value" aria-hidden>
              {MASK}
            </span>
            <span className="visually-hidden">{tr.infobar.netWorth.masked}</span>
          </>
        ) : (
          <span className="iw-nw-value">{formatTry(total)}</span>
        )}
        {!hideNetWorth && change !== null && (
          <span className="iw-nw-change" data-direction={change > 0 ? 'up' : change < 0 ? 'down' : 'flat'} title={tr.infobar.netWorth.changeToday}>
            {change > 0 ? '▲' : change < 0 ? '▼' : '–'} {formatPercent(change)}
          </span>
        )}
      </button>
      <button
        type="button"
        className="infobar-button iw-nw-eye"
        aria-pressed={!hideNetWorth}
        aria-label={hideNetWorth ? tr.infobar.netWorth.reveal : tr.infobar.netWorth.conceal}
        onClick={() => usePrefsStore.getState().update({ hideNetWorth: !hideNetWorth })}
      >
        {hideNetWorth ? <EyeOff size={15} aria-hidden /> : <Eye size={15} aria-hidden />}
      </button>
    </div>
  );
}
